import { useState, useEffect } from "react";
import {
  Container,
  Card,
  CardContent,
  Collapse,
  IconButton,
  Typography,
  Button,
  CircularProgress,
  TextField,
  InputAdornment,
  Box,
  Chip,
  Fade,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Map as MapIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import Config from "../config.json";

// Styled components
const StyledCard = styled(Card)(({ theme, expanded }) => ({
  marginBottom: 16,
  borderRadius: 16,
  border: "none",
  boxShadow: expanded 
    ? "0 8px 32px rgba(46, 46, 46, 0.15)" 
    : "0 4px 20px rgba(0, 0, 0, 0.08)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  overflow: "visible",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 12px 40px rgba(0, 0, 0, 0.12)",
  },
}));

const StyledCardHeader = styled(Box)(({ theme, expanded }) => ({
  padding: "20px 24px",
  backgroundColor: expanded ? "#f8f9fa" : "#2e2e2e",
  cursor: "pointer",
  borderRadius: expanded ? "16px 16px 0 0" : "16px",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  "&:hover": {
    backgroundColor: expanded ? "#f0f1f2" : "#1a1a1a",
  },
}));

const ExpandIcon = styled(ExpandMoreIcon)(({ expanded }) => ({
  transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
  transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
}));

export default function GeneralisasiClariifications() {
  const [expanded, setExpanded] = useState(null);
  const [steps, setSteps] = useState([]);
  const [filteredSteps, setFilteredSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  const url_list = Config.api_domain + "/jadwal_4/active/";

  const handleExpandClick = (index) => {
    setExpanded(expanded === index ? null : index);
  };

  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchQuery(query);
    
    if (query.trim() === "") {
      setFilteredSteps(steps);
    } else {
      const filtered = steps.filter(
        (step) =>
          step.title.toLowerCase().includes(query.toLowerCase()) ||
          step.description.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredSteps(filtered);
      setExpanded(null); // Close all expanded items when searching
    }
  };

  const clearSearch = () => {
    setSearchQuery("");
    setFilteredSteps(steps);
    setExpanded(null);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(url_list, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });
        const json = await res.json();
        const data = json.data;

        const mappedSteps = data.map((item) => ({
          title: item.judul,
          description: item.label,
          link: item.link,
        }));

        setSteps(mappedSteps);
        setFilteredSteps(mappedSteps);
        setLoading(false);
      } catch (error) {
        console.error("Fetch error:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      {/* Header Section */}
      <Box sx={{ textAlign: "center", mb: 6 }}>
        <Box sx={{ 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "center", 
          gap: 2, 
          mb: 2 
        }}>
          <MapIcon sx={{ fontSize: 40, color: "#2e2e2e" }} />
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontSize: { xs: "1.8rem", md: "2.2rem" },
              fontWeight: 700,
              color: "#2e2e2e",
            }}
          >
            Generalisasi Clarifications
          </Typography>
        </Box>
        
        
      </Box>

      {/* Search Section */}
      <Box sx={{ mb: 6, maxWidth: "600px", margin: "0 auto" }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search clarifications..."
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#666" }} />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton
                  onClick={clearSearch}
                  size="small"
                  sx={{
                    color: "#666",
                    "&:hover": { backgroundColor: "rgba(46, 46, 46, 0.08)" }
                  }}
                >
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "16px",
              backgroundColor: "#fafafa",
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: "#f5f5f5",
              },
              "&.Mui-focused": {
                backgroundColor: "#fff",
                boxShadow: "0 4px 20px rgba(46, 46, 46, 0.1)",
              },
              "& fieldset": {
                borderColor: "rgba(46, 46, 46, 0.2)",
                borderWidth: "2px",
              },
              "&:hover fieldset": {
                borderColor: "rgba(46, 46, 46, 0.3)",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#2e2e2e",
              },
            },
            "& .MuiInputBase-input": {
              padding: "16px 0",
              fontSize: "1rem",
            },
          }}
        />
        
        {/* Search Results Counter */}
        {searchQuery && (
          <Fade in={true}>
            <Box sx={{ mt: 3, textAlign: "center" }}>
              <Chip
                label={`${filteredSteps.length} result${filteredSteps.length !== 1 ? 's' : ''} found`}
                size="small"
                sx={{
                  backgroundColor: "rgba(46, 46, 46, 0.1)",
                  color: "#2e2e2e",
                  fontWeight: 600,
                }}
              />
            </Box>
          </Fade>
        )}
      </Box>

      {/* FAQ Content */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress sx={{ color: "#2e2e2e" }} size={50} />
        </Box>
      ) : filteredSteps.length === 0 ? (
        <Fade in={true}>
          <Box sx={{ textAlign: "center", py: 8 }}>
            <Typography variant="h6" sx={{ color: "#666", mb: 2 }}>
              No clarifications found matching your search
            </Typography>
            <Button
              onClick={clearSearch}
              variant="outlined"
              sx={{
                borderColor: "#2e2e2e",
                color: "#2e2e2e",
                borderRadius: "12px",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "rgba(46, 46, 46, 0.08)",
                },
              }}
            >
              Clear Search
            </Button>
          </Box>
        </Fade>
      ) : (
        <Fade in={true}>
          <Box>
            {filteredSteps.map((step, index) => (
              <StyledCard key={index} expanded={expanded === index}>
                <StyledCardHeader
                  expanded={expanded === index}
                  onClick={() => handleExpandClick(index)}
                >
                  <Typography
                    sx={{
                      fontSize: "1.1rem",
                      fontWeight: 600,
                      color: expanded === index ? "#2e2e2e" : "#fff",
                      flex: 1,
                      lineHeight: 1.4,
                      pr: 2,
                    }}
                  >
                    {step.title}
                  </Typography>
                  <IconButton
                    sx={{ 
                      color: expanded === index ? "#2e2e2e" : "#fff",
                      "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" }
                    }}
                  >
                    <ExpandIcon expanded={expanded === index} />
                  </IconButton>
                </StyledCardHeader>
                
                <Collapse 
                  in={expanded === index} 
                  timeout={400}
                  unmountOnExit
                >
                  <CardContent 
                    sx={{ 
                      padding: "24px",
                      backgroundColor: "#fafafa",
                      borderRadius: "0 0 16px 16px",
                      maxHeight: "400px",
                      overflowY: "auto",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "1rem",
                        lineHeight: 1.7,
                        color: "#444",
                        whiteSpace: "pre-line",
                        wordBreak: "break-word",
                        mb: step.link ? 3 : 0,
                      }}
                    >
                      {step.description}
                    </Typography>

                    {step.link && (
                      <Button
                        variant="contained"
                        endIcon={<OpenInNewIcon />}
                        href={step.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          backgroundColor: "#2e2e2e",
                          color: "white",
                          borderRadius: "12px",
                          textTransform: "none",
                          fontWeight: 600,
                          padding: "10px 20px",
                          boxShadow: "0 4px 16px rgba(46, 46, 46, 0.3)",
                          "&:hover": {
                            backgroundColor: "#1a1a1a",
                            transform: "translateY(-2px)",
                            boxShadow: "0 6px 20px rgba(46, 46, 46, 0.4)",
                          },
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        }}
                      >
                        Kunjungi Situs
                      </Button>
                    )}
                  </CardContent>
                </Collapse>
              </StyledCard>
            ))}
          </Box>
        </Fade>
      )}
      
      {/* Footer info */}
      {!loading && filteredSteps.length > 0 && (
        <Box sx={{ textAlign: "center", mt: 6, pt: 4, borderTop: "1px solid #eee" }}>
          
        </Box>
      )}
    </Container>
  );
}